import React, {useEffect, useState} from 'react';
import {Box, Button, Card, CardContent, Grid, List, ListItem, ListItemText, Typography, useToastProvider} from '@deere/fuel-react';
import "../assets/ListItem.scss";
import {useForm} from "react-hook-form";
import {useHistory, useParams} from 'react-router-dom';
import {toCapitalizeSplitStr} from '../shared/utils';
import {useAuthConfig} from "../shared/helper";
import {useOktaAuth} from '@okta/okta-react';
import {FeedbackComments} from './FeedbackComments';
import {FeedbackDeclineModal} from "../shared/modal/FeedbackDeclineModal";
import {Loading} from "../shared/Loading";
import {NoCriteriaInfo} from '../shared/NoCriteriaInfo';
import {FeedbackRespType} from "./FeedbackRespType";
import "../assets/FeedbackResponse.json";
import {ErrorPage} from "../shared/ErrorPage";
import {FEEDBACK_FORM_STATE} from "../shared/Constants";

const initialBoxState = {
    "quality": false, "delivery": false, "process": false, "relationship": false, "value": false
}

export const FeedbackForm = ({name}) => {
    let history = useHistory();
    const {authState} = useOktaAuth();
    let authAxios = useAuthConfig();
    const {enqueueToast} = useToastProvider();
    let {editFeedbackId} = useParams();
    const {control, handleSubmit, getValues, formState: {errors}} = useForm();
    const [categoriesList, setCategoriesList] = useState([]);
    const [state, setState] = useState(FEEDBACK_FORM_STATE);
    const [isLoading, setIsLoading] = useState(false);
    const [isBadRequestError, setIsBadRequestError] = useState(false);
    const [errorInfo, setErrorInfo] = useState("");
    const [showBoxes, setShowBoxes] = useState(initialBoxState)
    const errorBoxProps = {border: 4, borderColor: 'red', borderRadius: 5}

    useEffect(() => {
        //const validation =(event) => {
            // This will see if there's errors and if there are, show a toast with which categories the
            // errors are in. It is messy.
            if (Object.keys(errors).length > 0) {
                // The errors don't have the criteria id's, we have to parse them from a string
                // that looks like "[id]ratingStatus" and "[id]feedbackStatus" and convert it to a number
                //let missingIds = Object.keys(errors).map(val => Number(val.split("ratingStatus")[0]))
                let missingIds = Object.keys(errors).map(val => Number(val.replace("ratingStatus", "").replace("feedbackStatus", "")))

                // Next, we loop over our known criteria and look for the id's we found earlier. If we find
                // one and we haven't seen it already, we add the criteria category name (aka description) to our array
                let missingFields = []
                state.criteriaData.forEach(val => {


                    if (missingIds.includes(val.id) && !missingFields.includes(val.category.description)) {
                          // For category description changed the process alignment to process.
                        missingFields.push(val.category.description === "Process Alignment"?"process": val.category.description )
                    }
                })

                // Now, we want to create a red box around any error categories. We loop over our
                // problematic category names and update a useState object. This object will then
                // make red boxes appear around the different categories on the next render
                setShowBoxes(initialBoxState) // reset state since some categories could've been fixed
                missingFields.forEach(field => {
                    // We're updating the state in a loop, so we need to use the previous state before render
                    setShowBoxes(showBoxes => ({...showBoxes, [field.toLowerCase()]: true}))
                })

                // Finally, we have an array of strings with our error categories we can create the Toast
                // error message. The message inside the toast needs to show the problematic categories
                // on separate lines. So, we create a new HTML element and bold the categories for good measure
                enqueueToast({
                    variant: 'error',
                    title: 'Error',
                    message: (
                        <>
                            Please complete all required questions in categories: {missingFields.map((a, i) => {
                            return <React.Fragment key={i}><br/><b>{a}</b></React.Fragment>
                        })}
                        </>
                    ),
                    isPersistent: true
                })
            }
            // eslint-disable-next-line react-hooks/exhaustive-deps
            }, [errors]);
        //}

    const updateDirtyData = (currentStateData, criteriaId, isSelected) => {
        return currentStateData.map(row => {
            if (row.id === criteriaId) {
                row.isDirty = isSelected
            }
            return row
        });
    }

    const addDirtyField = (item) => {
        const isDataUpdated = item.feedback.notApplicable || item.feedback.rating !== "" || item.feedback.yesNoChoice !== ""
        const isUpdated = {isDirty: !!isDataUpdated}
        item = {...isUpdated, ...item}
        return item
    }

    const handleChange = (categoryId, criteriaId, isSelected) => {
        let categoryData = ""
        if (categoryId === 1) categoryData = "qualityCriteriaData"
        else if (categoryId === 2) categoryData = "deliverCriteriaData"
        else if (categoryId === 3) categoryData = "processAlignmentCriteriaData"
        else if (categoryId === 4) categoryData = "relationshipCriteriaData"
        else if (categoryId === 5) categoryData = "valueCreationCriteriaData"

        const updatedData = updateDirtyData(state[categoryData], criteriaId, isSelected)
        setState({
            ...state,
            [categoryData]: updatedData
        })
    };

    const handleDeclineRequest = (data) => {
        if (data.replacementsName) {
            data.comments = data.comments + " " + data.replacementsName;
        }
        authAxios.post(`/feedback/decline/${state.campaignId}`, data)
            .then(res => {
                enqueueToast({
                    variant: "success",
                    title: "Feedback Request Declined",
                    isCloseable: true
                });
                setTimeout(() => {
                    history.replace(`/feedback`);
                }, 2000);
            }).catch((error) => {
                console.error(error);
                enqueueToast({
                    variant: "error",
                    title: "System Error",
                    isPersistent: true
                });
            });
    }

    const handleCancel = () => {
        history.push("/feedback");
    }

    const getCategories = async () => {
        try {
            return await authAxios.get(`/categories`);
        } catch (e) {
            console.error(e);
        }
    };

    const getCampaignData = async (id) => {
        return await authAxios.get(`feedbacks/${id}`);
    }

    const processData = data => {
        setState({
            ...state,
            title: data.campaignTitle,
            campaignId: data.campaignId,
            supplierName: data.supplierName,
            supplierNumber: data.supplierNumber,
            period: data.period,
            fiscalYear: data.fiscalYear,
            dueDate: data.dueDate,
            requestedByName: data.requestedByName,
            requestedById: data.requestedById,
            responseStatus: data.responseStatus,
            qualityCriteriaData: data.criteria.filter(item => item.category.id === 1).map(item => addDirtyField(item)),
            deliverCriteriaData: data.criteria.filter(item => item.category.id === 2).map(item => addDirtyField(item)),
            processAlignmentCriteriaData: data.criteria.filter(item => item.category.id === 3).map(item => addDirtyField(item)),
            relationshipCriteriaData: data.criteria.filter(item => item.category.id === 4).map(item => addDirtyField(item)),
            valueCreationCriteriaData: data.criteria.filter(item => item.category.id === 5).map(item => addDirtyField(item)),
            criteriaData: data.criteria,
            qualityGeneralComments: data.generalComments.filter(item => item.categoryId === "1").length > 0 ? data.generalComments.filter(item => item.categoryId === "1")[0].comment : "",
            deliveryGeneralComments: data.generalComments.filter(item => item.categoryId === "2").length > 0 ? data.generalComments.filter(item => item.categoryId === "2")[0].comment : "",
            processGeneralComments: data.generalComments.filter(item => item.categoryId === "3").length > 0 ? data.generalComments.filter(item => item.categoryId === "3")[0].comment : "",
            relationshipGeneralComments: data.generalComments.filter(item => item.categoryId === "4").length > 0 ? data.generalComments.filter(item => item.categoryId === "4")[0].comment : "",
            valueCreationGeneralComments: data.generalComments.filter(item => item.categoryId === "5").length > 0 ? data.generalComments.filter(item => item.categoryId === "5")[0].comment : "",
        });
    }

    const onSubmit = (submitFlag) => {
        const feedbackObj = state.criteriaData.map(item => {
            return {
                id: item.id,
                feedback: {
                    notApplicable: getValues(`${item.id}n/a`),
                    yesNoChoice: !getValues(`${item.id}n/a`) && getValues(`${item.id}feedbackStatus`) ? getValues(`${item.id}feedbackStatus`) : '',
                    rating: !getValues(`${item.id}n/a`) && getValues(`${item.id}ratingStatus`) ? getValues(`${item.id}ratingStatus`) : '',
                    comment: getValues(`${item.id}qualityComments`) ? getValues(`${item.id}qualityComments`) : getValues(`${item.id}ratingComments`) ? getValues(`${item.id}ratingComments`) : '',
                }
            }
        })
        const isSubmit = submitFlag === "submit"
        const generalComments = [
            {
                "categoryId": "1",
                "comment": getValues('qualityGeneralComments')
            },
            {
                "categoryId": "2",
                "comment": getValues('deliveryGeneralComments')
            },
            {
                "categoryId": "3",
                "comment": getValues('processGeneralComments')
            },
            {
                "categoryId": "4",
                "comment": getValues('relationshipGeneralComments')
            },
            {
                "categoryId": "5",
                "comment": getValues('valueCreationGeneralComments')
            }
        ]

        const formData = {
            criteria: [...feedbackObj], generalComments, isSubmit
        }

        authAxios.post(`/feedback/${state.campaignId}`, formData)
            .then(res => {
                // console.log(res.data)
                if (isSubmit) {
                    enqueueToast({
                        variant: "success",
                        title: "Feedback Submitted",
                        isCloseable: true
                    });
                    setTimeout(() => {
                        history.replace(`/feedback`);
                    }, 2000);
                } else {
                    enqueueToast({
                        variant: "success",
                        title: "Saved Successfully",
                        isCloseable: true
                    });
                }
            }).catch((error) => {
                enqueueToast({
                  variant: "error",
                  title: error.response.data.error ?? "",
                  message: error.response.data.message ?? "",
                  isPersistent: true
                });
            });
    }

    const handleClick = (index) => {
        let ele;
        switch (index) {
            case 0:
                ele = document.getElementById("#quality")
                break;
            case 1:
                ele = document.getElementById("#delivery")
                break;
            case 2:
                ele = document.getElementById("#process")
                break;
            case 3:
                ele = document.getElementById("#relationship")
                break;
            case 4:
                ele = document.getElementById("#value")
                break;
            default:
                ele = document.getElementById("#quality")
                break;
        }
        ele.scrollIntoView({behavior: "smooth"})
    };

    useEffect(() => {
        setIsLoading(true)
        getCategories().then(res => {
            setCategoriesList(res.data);
        }).catch((error) => {
            console.error(error)
        });
        if (editFeedbackId) {
            getCampaignData(editFeedbackId).then(res => {
                const data = res.data;
                if (Object.keys(res.data).length === 0) {
                    setErrorInfo("This feedback response has been declined.")
                    setIsBadRequestError(true)
                } else if (data.responseStatus.id === 2) {
                    setErrorInfo("Response already submitted.")
                    setIsBadRequestError(true)
                    setIsLoading(false)
                } else
                    processData(data)
                setIsLoading(false)
            }).catch((error) => {
                console.error(error)
                setErrorInfo("Sorry, you don't have access to this feature.")
                setIsBadRequestError(true)
                setIsLoading(false)
            })
        }
        return () => {
            setCategoriesList(null);
            setIsBadRequestError(false)
        } // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [authState]);

    return (
        <>
            {isLoading && (
                <Loading />
            )}
            {!isLoading && !isBadRequestError && (
                <form onSubmit={handleSubmit((data) => onSubmit("submit"))}>
                    <Grid container>
                        <Grid item xs={12} md={7} className="sticky-header">
                            <Typography variant="h1" component="h1">
                                {state.title ? state.title : `${toCapitalizeSplitStr(state.supplierName)} - ${parseInt(state.supplierNumber)}`}
                            </Typography>
                            <Typography variant="h4">
                                Requested By {state.requestedByName} - {state.requestedById}, response due
                                by {state.dueDate}
                            </Typography>
                        </Grid>
                        <Grid item xs={12} md={5} className="sticky-header">
                            <Box display="flex" justifyContent="flex-end">
                                <FeedbackDeclineModal
                                    onSave={handleDeclineRequest}
                                />
                                <Button variant="secondary" style={{marginRight: "1rem"}}
                                    onClick={() => handleCancel()} size="large">Cancel</Button>
                                <Button variant="secondary" style={{marginRight: "1rem"}} size="large"
                                    onClick={() => onSubmit("save")}>Save</Button>
                                <Button variant="primary" size="large" type="submit">
                                    Save & Submit
                                </Button>
                            </Box>
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <Box mt={6} mb={2} style={{position: "sticky", top: 80}}>
                                <Card>
                                    <CardContent style={{padding: 0}}>
                                        <Box>
                                            <Grid container alignItems="flex-start" spacing={6}>
                                                <Grid item xs={12}>
                                                    <List disablePadding component="nav"
                                                        aria-label="category list">
                                                        {categoriesList.map((item, index) => {
                                                            return (
                                                                <ListItem button
                                                                    divider={categoriesList.length !== index + 1}
                                                                    key={index}
                                                                    className="feedback-List"
                                                                    onClick={() => handleClick(index)}
                                                                >
                                                                    <ListItemText
                                                                        primary={
                                                                            <React.Fragment>
                                                                                <Grid container spacing={6}>
                                                                                    <Grid item xs={9}>
                                                                                        <Typography size="lg"
                                                                                            component="p"
                                                                                        >
                                                                                            {item.description}
                                                                                        </Typography>
                                                                                    </Grid>
                                                                                    <Grid item xs={3}>
                                                                                        <Box display="flex"
                                                                                            justifyContent="flex-end">
                                                                                            <Typography
                                                                                                size="lg"
                                                                                                component="p"
                                                                                            >
                                                                                                {item.id === 1 ? `${state.qualityCriteriaData.filter(row => row.isDirty).length} / ${state.qualityCriteriaData.length}` : ''}
                                                                                                {item.id === 2 ? `${state.deliverCriteriaData.filter(row => row.isDirty).length} / ${state.deliverCriteriaData.length}` : ''}
                                                                                                {item.id === 3 ? `${state.processAlignmentCriteriaData.filter(row => row.isDirty).length} / ${state.processAlignmentCriteriaData.length}` : ''}
                                                                                                {item.id === 4 ? `${state.relationshipCriteriaData.filter(row => row.isDirty).length} / ${state.relationshipCriteriaData.length}` : ''}
                                                                                                {item.id === 5 ? `${state.valueCreationCriteriaData.filter(row => row.isDirty).length} / ${state.valueCreationCriteriaData.length}` : ''}
                                                                                            </Typography>
                                                                                        </Box>
                                                                                    </Grid>
                                                                                </Grid>
                                                                            </React.Fragment>
                                                                        }
                                                                    />
                                                                </ListItem>
                                                            );
                                                        })}
                                                    </List>
                                                </Grid>
                                            </Grid>
                                        </Box>
                                    </CardContent>
                                </Card>
                            </Box>
                        </Grid>
                        <Grid item xs={12} md={9}>
                            <Box m={6} id="#quality">
                                <Card>
                                    <CardContent style={{padding: 0}}>
                                        <Box p={4} {...(showBoxes.quality ? errorBoxProps : {})}>
                                            <Grid container alignItems="center" spacing={4}>
                                                <Grid item xs={12}>
                                                    <Typography variant="h2">
                                                        Quality
                                                    </Typography>
                                                </Grid>
                                                {state.qualityCriteriaData && state.qualityCriteriaData.length ?
                                                    state.qualityCriteriaData && state.qualityCriteriaData.map((item, index) => {
                                                        return (
                                                           // <Box border={4} borderColor={'red'} borderRadius={3}>
                                                                <FeedbackRespType
                                                                    key={index}
                                                                    state={state}
                                                                    control={control}
                                                                    onChange={handleChange}
                                                                    item={item}
                                                                    errors={errors}
                                                                />
                                                            //</Box>
                                                        );
                                                    }) :
                                                    <NoCriteriaInfo />
                                                }
                                                <Grid item xs={12}>
                                                    <FeedbackComments
                                                        control={control}
                                                        showLabel={true}
                                                        name={"qualityGeneralComments"}
                                                        generalComments={state.qualityGeneralComments}
                                                        placeholder={'Any additional comments about “Quality”?'}
                                                    />
                                                </Grid>
                                            </Grid>
                                        </Box>
                                    </CardContent>
                                </Card>
                            </Box>
                            <Box m={6} id="#delivery">
                                <Card>
                                    <CardContent style={{padding: 0}}>
                                        <Box p={4} {...(showBoxes.delivery ? errorBoxProps : {})}>
                                            <Grid container alignItems="center" spacing={4}>
                                                <Grid item xs={12}>
                                                    <Typography variant="h2">
                                                        Delivery
                                                    </Typography>
                                                </Grid>
                                                {state.deliverCriteriaData && state.deliverCriteriaData.length ?
                                                    state.deliverCriteriaData && state.deliverCriteriaData.map((item, index) => {
                                                        return (
                                                            <FeedbackRespType
                                                                key={index}
                                                                state={state}
                                                                control={control}
                                                                onChange={handleChange}
                                                                item={item}
                                                                errors={errors}
                                                            />
                                                        );
                                                    }) :
                                                    <NoCriteriaInfo />
                                                }
                                                <Grid item xs={12}>
                                                    <FeedbackComments
                                                        control={control}
                                                        showLabel={true}
                                                        name={"deliveryGeneralComments"}
                                                        generalComments={state.deliveryGeneralComments}
                                                        placeholder={'Any additional comments about “Delivery”?'}
                                                    />
                                                </Grid>
                                            </Grid>
                                        </Box>
                                    </CardContent>
                                </Card>
                            </Box>
                            <Box m={6} id="#process">
                                <Card>
                                    <CardContent style={{padding: 0}}>
                                        <Box p={4} {...(showBoxes.process ? errorBoxProps : {})}>
                                            <Grid container alignItems="center" spacing={4}>
                                                <Grid item xs={12}>
                                                    <Typography variant="h2">
                                                        Process Alignment
                                                    </Typography>
                                                </Grid>
                                                {state.processAlignmentCriteriaData && state.processAlignmentCriteriaData.length ?
                                                    state.processAlignmentCriteriaData && state.processAlignmentCriteriaData.map((item, index) => {
                                                        return (
                                                            <FeedbackRespType
                                                                key={index}
                                                                state={state}
                                                                control={control}
                                                                onChange={handleChange}
                                                                item={item}
                                                                errors={errors}
                                                            />
                                                        )
                                                    }) :
                                                    <NoCriteriaInfo />
                                                }
                                                <Grid item xs={12}>
                                                    <FeedbackComments
                                                        control={control}
                                                        showLabel={true}
                                                        name={"processGeneralComments"}
                                                        generalComments={state.processGeneralComments}
                                                        placeholder={'Any additional comments about “Process Alignment”?'}
                                                    />
                                                </Grid>
                                            </Grid>
                                        </Box>
                                    </CardContent>
                                </Card>
                            </Box>
                            <Box m={6} id="#relationship">
                                <Card>
                                    <CardContent style={{padding: 0}}>
                                        <Box p={4} {...(showBoxes.relationship ? errorBoxProps : {})}>
                                            <Grid container alignItems="center" spacing={4}>
                                                <Grid item xs={12}>
                                                    <Typography variant="h2">
                                                        Relationship
                                                    </Typography>
                                                </Grid>
                                                {state.relationshipCriteriaData && state.relationshipCriteriaData.length ?
                                                    state.relationshipCriteriaData && state.relationshipCriteriaData.map((item, index) => {
                                                        return (
                                                            <FeedbackRespType
                                                                key={index}
                                                                state={state}
                                                                control={control}
                                                                onChange={handleChange}
                                                                item={item}
                                                                errors={errors}
                                                            />
                                                        )
                                                    }) :
                                                    <NoCriteriaInfo />
                                                }
                                                <Grid item xs={12}>
                                                    <FeedbackComments
                                                        control={control}
                                                        showLabel={true}
                                                        name={"relationshipGeneralComments"}
                                                        generalComments={state.relationshipGeneralComments}
                                                        placeholder={'Any additional comments about “Relationship”?'}
                                                    />
                                                </Grid>
                                            </Grid>
                                        </Box>
                                    </CardContent>
                                </Card>
                            </Box>
                            <Box m={6} id="#value">
                                <Card>
                                    <CardContent style={{padding: 0}}>
                                        <Box p={4} {...(showBoxes.value ? errorBoxProps : {})}>
                                            <Grid container alignItems="center" spacing={4}>
                                                <Grid item xs={12}>
                                                    <Typography variant="h2">
                                                        Value Creation
                                                    </Typography>
                                                </Grid>
                                                {state.valueCreationCriteriaData && state.valueCreationCriteriaData.length ?
                                                    state.valueCreationCriteriaData && state.valueCreationCriteriaData.map((item, index) => {
                                                        return (
                                                            <FeedbackRespType
                                                                key={index}
                                                                state={state}
                                                                control={control}
                                                                onChange={handleChange}
                                                                item={item}
                                                                errors={errors}
                                                            />
                                                        )
                                                    }) :
                                                    <NoCriteriaInfo />
                                                }
                                                <Grid item xs={12}>
                                                    <FeedbackComments
                                                        control={control}
                                                        showLabel={true}
                                                        name={"valueCreationGeneralComments"}
                                                        generalComments={state.valueCreationGeneralComments}
                                                        placeholder={'Any additional comments about “Value Creation”?'}
                                                    />
                                                </Grid>
                                            </Grid>
                                        </Box>
                                    </CardContent>
                                </Card>
                            </Box>
                        </Grid>
                    </Grid>
                </form>
            )}
            {!isLoading && isBadRequestError && (
                <ErrorPage
                    primaryMsg={errorInfo}
                    secondaryMsg={"No Further Action is Required"}
                    backPage={"feedback"} />
            )}
        </>
    );
}
